import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { epcSubcontractAdminService } from '@/services/epcSubcontractAdmin.service';
import { useProjectsForSelection } from '@/hooks/useProject';
import { formatMoney } from '@/utils/format';

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Active', label: 'Active' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Rejected', label: 'Rejected' },
] as const;

function statusTagColor(status: string): string {
  switch (status) {
    case 'Draft':
      return 'default';
    case 'Submitted':
      return 'processing';
    case 'Approved':
      return 'success';
    case 'Active':
      return 'cyan';
    case 'Closed':
      return 'default';
    case 'Rejected':
      return 'error';
    default:
      return 'default';
  }
}

function shortId(id: unknown): string {
  const s = String(id || '');
  if (!s) return '—';
  return s.length > 8 ? `${s.slice(0, 8)}…` : s;
}

function apiErr(e: unknown): string {
  const ax = e as {
    response?: { data?: { message?: string; errors?: { message?: string } } };
    message?: string;
  };
  const data = ax?.response?.data;
  return data?.message || data?.errors?.message || ax?.message || 'Request failed';
}

const EpcSubcontractsPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string | undefined>(undefined);

  const listQ = useQuery({
    queryKey: ['epc-subcontracts', statusFilter || null, projectFilter || null],
    queryFn: () =>
      epcSubcontractAdminService.listAgreements({
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(projectFilter ? { projectId: projectFilter } : {}),
      }),
    staleTime: 15_000,
  });
  const rows = (listQ.data || []) as Record<string, unknown>[];

  const { data: projects = [] } = useProjectsForSelection();
  const projectOptions = useMemo(
    () =>
      (projects || []).map((p) => ({
        value: p.id,
        label: p.project_number
          ? `${p.project_number} — ${p.project_name}`
          : p.project_name || p.id,
      })),
    [projects]
  );
  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects || []) {
      m.set(p.id, p.project_number ? `${p.project_number}` : p.project_name || shortId(p.id));
    }
    return m;
  }, [projects]);

  const submitM = useMutation({
    mutationFn: (id: string) => epcSubcontractAdminService.submitAgreement(id),
    onSuccess: () => {
      message.success('Agreement submitted');
      qc.invalidateQueries({ queryKey: ['epc-subcontracts'] });
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Subcontractor Agreements
          </Title>
          <Text type="secondary">
            Buy-side SCA register. Draft → Submit → Approve → Active. Open workspace for SPC,
            certification, variations, claims, and closeout.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/epc/commercial/subcontracts/new')}
        >
          New Agreement
        </Button>
      </Space>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select
            style={{ width: 160 }}
            value={statusFilter}
            options={[...STATUS_OPTIONS]}
            onChange={(v) => setStatusFilter(v ?? '')}
            placeholder="Status"
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 240 }}
            placeholder="Project (optional)"
            value={projectFilter}
            options={projectOptions}
            onChange={(v) => setProjectFilter(v)}
          />
        </Space>
      </Card>

      <Card>
        <Table
          loading={listQ.isLoading}
          rowKey={(r) => String(r.id)}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No subcontractor agreements yet"
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/epc/commercial/subcontracts/new')}
                >
                  Create first agreement
                </Button>
              </Empty>
            ),
          }}
          columns={[
            {
              title: 'Number',
              dataIndex: 'agreement_number',
              width: 140,
              render: (v, r) => (
                <Link to={`/epc/commercial/subcontracts/${r.id}`}>
                  {v ? String(v) : '—'}
                </Link>
              ),
            },
            {
              title: 'Title',
              dataIndex: 'title',
              ellipsis: true,
              render: (v) => (v ? String(v) : '—'),
            },
            {
              title: 'Supplier',
              key: 'supplier',
              width: 180,
              ellipsis: true,
              render: (_: unknown, r) => {
                if (r.supplier_name) return String(r.supplier_name);
                return shortId(r.supplier_id);
              },
            },
            {
              title: 'Project',
              key: 'project',
              width: 140,
              ellipsis: true,
              render: (_: unknown, r) => {
                if (r.project_name) return String(r.project_name);
                if (r.project_number) return String(r.project_number);
                const pid = r.project_id ? String(r.project_id) : '';
                if (!pid) return '—';
                return projectNameById.get(pid) || shortId(pid);
              },
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (s: string) => <Tag color={statusTagColor(s)}>{s || '—'}</Tag>,
            },
            {
              title: 'Sum',
              dataIndex: 'contract_sum',
              width: 130,
              render: (v, r) =>
                v != null
                  ? formatMoney(Number(v), { currency: String(r.currency || 'AED') })
                  : '—',
            },
            {
              title: 'Actions',
              key: 'actions',
              width: 200,
              render: (_: unknown, r) => (
                <Space size="small" onClick={(e) => e.stopPropagation()}>
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/epc/commercial/subcontracts/${r.id}`)}
                  >
                    Open
                  </Button>
                  {String(r.status) === 'Draft' || String(r.status) === 'Rejected' ? (
                    <>
                      <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() => navigate(`/epc/commercial/subcontracts/${r.id}/edit`)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="link"
                        style={{ padding: 0 }}
                        loading={submitM.isPending}
                        onClick={() => submitM.mutate(String(r.id))}
                      >
                        Submit
                      </Button>
                    </>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default EpcSubcontractsPage;
